import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import JobDetails from '../../pages/JobDetails';

export default function JobDetailsSlidePanel({ 
  isOpen, 
  onClose, 
  jobId,
  // Allow override of these if needed
  isPublicView = false,
  accessToken = null,
  onJobStatusChange = null
}) {
  const navigate = useNavigate();
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Close panel on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle rendering state for animations
  useEffect(() => {
    if (isOpen && jobId) {
      setShouldRender(true);
      // Start animation after mounting
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      // Start exit animation immediately
      setIsAnimating(false);
      // Delay unmounting to allow exit animation
      const timer = setTimeout(() => setShouldRender(false), 440);
      return () => clearTimeout(timer);
    }
  }, [isOpen, jobId]);

  // Don't render anything if should not render
  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Slide Panel */}
      <div 
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-4xl bg-sky-950 lg:bg-white shadow-xl transform transition-transform duration-[440ms] ease-in-out md:max-w-5xl lg:max-w-6xl ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)'
        }}
      >
        
        {/* Close Button - Fixed position */}
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
            aria-label="Close job details"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Share Button - Fixed position (only for authenticated view) */}
        {!isPublicView && (
          <div className="absolute top-4 left-16 z-10">
            <button
              onClick={() => {
                // Update URL to show the direct job page, then share
                const jobUrl = `${window.location.origin}/dashboard/jobs/${jobId}`;
                window.open(jobUrl, '_blank');
              }}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
              aria-label="Open job in new tab"
              title="Open in new tab to share"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Content Container - Full height with scroll */}
        <div className="h-full overflow-y-auto">
          <div className="pt-16 pb-6">
            {/* Only render JobDetails when panel is open to avoid unnecessary API calls */}
            {isAnimating && jobId && (
              <JobDetails 
                isPublicView={isPublicView}
                accessToken={accessToken}
                jobId={jobId}
                onJobStatusChange={onJobStatusChange}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
} 